/**
 * Screen reader and keyboard support that sits on top of the theme's components.
 *
 * 1. Pop-ups and drawers (x-modal, x-drawer, the side menu...). The theme marks them role="dialog" aria-modal="true"
 *    and traps keyboard focus, but on touch devices it deliberately leaves focus where it was, so VoiceOver on iPhone
 *    stays on the page behind. When one opens we:
 *      - give it a name from its header if it has none,
 *      - make the rest of the page inert, so swiping stays inside the pop-up,
 *      - move focus to its [data-dialog-initial-focus] element (or its heading, or its close button),
 *    and when it closes we restore the page and return focus to the button that opened it.
 * 2. Announcement bar: a pause/play button for the rotating messages.
 * 3. Variant pickers: choosing an option is announced through the theme's polite status region, including when the
 *    choice is sold out ("Size M, sold out").
 */
(() => {
  const SKIPPED_TAGS = ['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'NOSCRIPT', 'META'];
  const openerByDialog = new WeakMap();
  const inertByDialog = new WeakMap();
  let lastControl = null;

  const isModalDialog = (element) =>
    element instanceof HTMLElement && element.getAttribute('role') === 'dialog' && element.getAttribute('aria-modal') === 'true';

  function announce(message) {
    const region = document.getElementById('status-announcement');
    if (!region || !message) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 100);
  }

  // Remember which control was used, so focus can return to it when the dialog closes
  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[aria-controls]');
    if (control) lastControl = control;
  }, true);

  function ensureName(dialog) {
    if (dialog.hasAttribute('aria-label') || dialog.hasAttribute('aria-labelledby')) return;

    const header = dialog.querySelector(':scope > [slot="header"]');
    if (header && header.textContent.trim()) {
      header.id = header.id || `${dialog.id || 'dialog'}-title`;
      dialog.setAttribute('aria-labelledby', header.id);
    }
  }

  function initialFocusTarget(dialog) {
    const isShown = (element) => element && element.getClientRects().length > 0;
    const candidates = [
      dialog.querySelector('[data-dialog-initial-focus]'),
      dialog.shadowRoot?.querySelector('[part~="close-button"]'),
      dialog.querySelector('button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
    ];
    return candidates.find(isShown);
  }

  function onDialogOpen(dialog) {
    ensureName(dialog);

    const opener = lastControl?.getAttribute('aria-controls') === dialog.id ? lastControl : document.activeElement;
    openerByDialog.set(dialog, opener);

    // Only elements this dialog made inert are restored later, so stacked dialogs unwind correctly
    const madeInert = [];
    Array.from(document.body.children).forEach((element) => {
      if (element === dialog || element.contains(dialog) || element.inert || SKIPPED_TAGS.includes(element.tagName)) return;
      element.inert = true;
      madeInert.push(element);
    });
    inertByDialog.set(dialog, madeInert);

    requestAnimationFrame(() => {
      if (!dialog.hasAttribute('open') || dialog.contains(document.activeElement) || dialog.shadowRoot?.activeElement) return;
      initialFocusTarget(dialog)?.focus({ preventScroll: true });
    });
  }

  function onDialogClose(dialog) {
    (inertByDialog.get(dialog) || []).forEach((element) => { element.inert = false; });
    inertByDialog.delete(dialog);

    const opener = openerByDialog.get(dialog);
    openerByDialog.delete(dialog);

    dialog.addEventListener('dialog:after-hide', () => {
      setTimeout(() => {
        if (opener?.isConnected && !opener.closest('[inert]')) opener.focus({ preventScroll: true });
      }, 0);
    }, { once: true });
  }

  // The theme's dialogs dispatch a bubbling "toggle" event each time their "open" attribute changes
  document.addEventListener('toggle', (event) => {
    const dialog = event.target;
    if (!isModalDialog(dialog)) return;

    if (dialog.hasAttribute('open')) {
      onDialogOpen(dialog);
    } else {
      onDialogClose(dialog);
    }
  }, true);

  // Announcement bar: pause/play the rotation. Visitors who ask their device for reduced motion start paused.
  function setAnnouncementsPaused(button, paused) {
    const carousel = document.getElementById(button.getAttribute('aria-controls'));
    const player = carousel?.player;
    if (!player) return false;

    paused ? player.stop() : player.start();
    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    button.querySelector('.announcement-bar__pause-icon')?.toggleAttribute('hidden', paused);
    button.querySelector('.announcement-bar__play-icon')?.toggleAttribute('hidden', !paused);
    return true;
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-announcement-pause]');
    if (button) setAnnouncementsPaused(button, button.getAttribute('aria-pressed') !== 'true');
  });

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.addEventListener('load', () => {
      document.querySelectorAll('[data-announcement-pause]').forEach((button) => setAnnouncementsPaused(button, true));
    });
  }

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('variant-picker')) return;

    const label = input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
    const legend = input.closest('fieldset')?.querySelector('legend');
    const optionName = (legend?.firstChild?.textContent || '').replace(':', '').trim();
    const valueText = (label?.textContent || '').replace(/\s+/g, ' ').trim();

    if (valueText) announce(optionName ? `${optionName} ${valueText}` : valueText);
  });
})();
